import type { AudioGraph } from './graph.ts';

export type GraphPort = Pick<AudioGraph, 'attach' | 'detach' | 'beginUtterance' | 'endUtterance' | 'setHandsFree'>;

export type MicEvents = {
  /** A stream is being opened (`true`), or that attempt has settled (`false`). */
  opening(on: boolean): void;
  refused(error: unknown): void;
  /**
   * A hold ended before its stream opened, which is what a first-ever
   * permission prompt does, because the prompt takes the pointer
   * (media-channel design §4.4).
   */
  releasedBeforeOpen(): void;
  /** A stream is live, so any earlier refusal no longer describes the microphone. */
  opened(): void;
};

type StreamState = 'closed' | 'opening' | 'open';

/**
 * When a conversation's microphone is open (media-channel design §4.2): for
 * the length of an utterance, or for as long as hands-free is on. Phones put
 * a page with a live capture stream on the call channel, so every moment it
 * is open is a moment the tutor plays quieter and on the wrong volume (§1).
 *
 * The utterance, not the hold, decides the close. A release only asks the
 * worklet to end, and its `ended` follows every sample the hold produced
 * (`capture.ts`), so closing at the release would cut the last word.
 *
 * Closing is synchronous (`closeMicrophone` stops the tracks), so a stream
 * is never "closing": a press after a close simply opens a new one.
 */
export class SessionMicrophone {
  private state: StreamState = 'closed';
  private stream: MediaStream | null = null;
  private holding = false;
  private handsFree = false;
  private utterance = false;
  private releasedEarly = false;
  private disposed = false;
  // Assigned in the body: node's strip-only loader rejects parameter properties.
  private readonly graph: GraphPort;
  private readonly openStream: () => Promise<MediaStream>;
  private readonly closeStream: (stream: MediaStream) => void;
  private readonly events: MicEvents;

  constructor(
    graph: GraphPort,
    open: () => Promise<MediaStream>,
    close: (stream: MediaStream) => void,
    events: MicEvents,
  ) {
    this.graph = graph;
    this.openStream = open;
    this.closeStream = close;
    this.events = events;
  }

  /** A hold began, or a kid's first tap. */
  press(): void {
    if (this.disposed || this.holding) return;
    this.holding = true;
    this.releasedEarly = false;
    if (this.state === 'open') this.begin();
    else if (this.state === 'closed') this.open();
  }

  /** A hold ended, or a kid's second tap. */
  release(): void {
    if (this.disposed || !this.holding) return;
    this.holding = false;
    // `end` whenever a stream is open, not only while `utterance` says one
    // is: an earlier utterance's `ended` can land after this hold's `begin`
    // and clear the flag, and skipping the `end` would leave the worklet in a
    // manual hold that never replies. `Capture.end` answers an `end` for no
    // open utterance with nothing (final review, Important 1).
    if (this.state === 'opening') this.releasedEarly = true;
    else if (this.state === 'open') this.graph.endUtterance();
  }

  /** The worklet opened an utterance on its own: hands-free's onset. */
  started(): void {
    this.utterance = true;
  }

  /** The worklet closed an utterance, after its last sample. */
  ended(): void {
    this.utterance = false;
    this.closeIfIdle();
  }

  setHandsFree(on: boolean): void {
    if (this.disposed) return;
    this.handsFree = on;
    this.graph.setHandsFree(on);
    if (on && this.state === 'closed') this.open();
    else if (!on) this.closeIfIdle();
  }

  stop(): void {
    this.disposed = true;
    this.holding = false;
    this.handsFree = false;
    this.utterance = false;
    this.close();
  }

  private begin(): void {
    this.utterance = true;
    this.graph.beginUtterance();
  }

  private open(): void {
    this.state = 'opening';
    this.events.opening(true);
    this.openStream().then(
      (stream) => {
        this.events.opening(false);
        if (this.disposed || (!this.holding && !this.handsFree)) {
          this.state = 'closed';
          this.closeStream(stream);
          if (!this.disposed && this.releasedEarly) this.events.releasedBeforeOpen();
          return;
        }
        this.state = 'open';
        this.stream = stream;
        this.graph.attach(stream);
        this.events.opened();
        if (this.holding) this.begin();
      },
      (error: unknown) => {
        this.events.opening(false);
        this.state = 'closed';
        // What asked for the stream is over: the view drops the press and
        // hands-free on a refusal, and a hold left set here would swallow
        // the next press.
        this.holding = false;
        this.handsFree = false;
        if (!this.disposed) this.events.refused(error);
      },
    );
  }

  private closeIfIdle(): void {
    if (this.state === 'open' && !this.holding && !this.handsFree && !this.utterance) this.close();
  }

  // A stream still opening is closed by `open`'s continuation, which sees
  // `disposed`, or no hold and no hands-free.
  private close(): void {
    if (this.state !== 'open' || !this.stream) return;
    this.graph.detach();
    this.closeStream(this.stream);
    this.stream = null;
    this.state = 'closed';
  }
}
